import * as React from 'react';
import { useFormContext, useFieldArray, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { ProductPricesForm } from './product-prices-form';
import { FormImageUpload } from '@/components/form/form-image-upload';

const DEFAULT_VARIANT = {
  name: '',
  description: '',
  sku: '',
  imageUrl: '',
  grams: null,
  active: true,
  default: false,
  price: null,
  compareAtPrice: null,
  prices: [],
};

const DEFAULT_PRICE = { interval: 'month', intervalCount: 1, recurring: false, currencyOptions: {}, default: true };

interface ProductVariantsFormProps {
  storeId: string;
  /** Hide the "Add price" button inside each variant. */
  hideAddPrice?: boolean;
  /** Hide the remove-price button inside each variant. */
  hideRemovePrice?: boolean;
  /** Hide the "Add Variant" button. */
  hideAddVariant?: boolean;
  /** Hide the remove-variant button. */
  hideRemoveVariant?: boolean;
}

/**
 * Product variants form component
 * Handles product variants using React Hook Form useFieldArray
 */
export const ProductVariantsForm = ({
  storeId,
  hideAddPrice = false,
  hideRemovePrice = false,
  hideAddVariant = false,
  hideRemoveVariant = false,
}: ProductVariantsFormProps) => {
  const { t } = useTranslation(['products', 'common']);
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'variants',
  });

  const handleAddVariant = () => {
    const isFirstVariant = fields.length === 0;
    append({
      ...DEFAULT_VARIANT,
      default: isFirstVariant,
      // When adding prices is disabled, seed one so the variant stays valid.
      prices: hideAddPrice ? [{ ...DEFAULT_PRICE }] : [],
    });
  };

  const handleRemoveVariant = (index: number) => {
    const wasDefault = (fields[index] as any)?.default;
    remove(index);

    // If removing the default variant, set the first remaining variant as default
    if (wasDefault && fields.length > 1) {
      const firstField = fields[0] as any;
      update(0, { ...firstField, default: true });
    }
  };

  const handleSetDefault = (index: number) => {
    fields.forEach((field, i) => {
      const fieldData = field as any;
      update(i, { ...fieldData, default: i === index });
    });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{t('variants')}</CardTitle>
            <p className="text-muted-foreground mt-1 text-sm">
              {t('variantsDescription')}
            </p>
          </div>
          {!hideAddVariant && (
            <Button type="button" onClick={handleAddVariant} size="sm">
              <Plus className="mr-1 h-4 w-4" />
              {t('addVariant')}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Display variants field error */}
        {errors.variants && !Array.isArray(errors.variants) && (
          <p className="mt-1 text-xs leading-tight text-red-600">{errors.variants.message as string}</p>
        )}

        {fields.length === 0 && (
          <div className="text-muted-foreground py-8 text-center">
            <p>
              {t('noVariants')}
            </p>
          </div>
        )}

        {fields.map((field, index) => (
          <VariantCard
            key={field.id}
            field={field}
            index={index}
            onRemove={() => handleRemoveVariant(index)}
            onSetDefault={() => handleSetDefault(index)}
            canRemove={!hideRemoveVariant && fields.length > 1}
            storeId={storeId}
            control={control}
            errors={errors}
            hideAddPrice={hideAddPrice}
            hideRemovePrice={hideRemovePrice}
          />
        ))}
      </CardContent>
    </Card>
  );
};

interface VariantCardProps {
  field: any;
  index: number;
  onRemove: () => void;
  onSetDefault: () => void;
  canRemove: boolean;
  storeId: string;
  control: any;
  errors: any;
  hideAddPrice?: boolean;
  hideRemovePrice?: boolean;
}

/**
 * Variant card component
 * Renders a single variant form using Controller for proper form integration
 */
const VariantCard = ({
  field,
  index,
  onRemove,
  onSetDefault,
  canRemove,
  storeId,
  control,
  errors,
  hideAddPrice,
  hideRemovePrice,
}: VariantCardProps) => {
  const { t } = useTranslation(['products', 'common']);
  const variantErrors = errors?.variants?.[index];

  return (
    <Card className="relative border-2">
      <CardContent className="space-y-4 pt-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h4 className="font-medium">Variant {index + 1}</h4>
            {field.default && <Badge variant="default">{t('default')}</Badge>}
          </div>
          {canRemove && !field.default && (
            <Button type="button" variant="ghost" size="sm" onClick={onRemove} className="h-8 w-8 p-0">
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Controller
            name={`variants.${index}.name`}
            control={control}
            render={({ field: controlField }) => (
              <div className="space-y-2">
                <Label htmlFor={`variant-${index}-name`}>
                  {t('name')} *
                </Label>
                <Input id={`variant-${index}-name`} {...controlField} />
                {variantErrors?.name && (
                  <p className="mt-1 text-xs leading-tight text-red-600">
                    {variantErrors.name.message as string}
                  </p>
                )}
              </div>
            )}
          />

          <Controller
            name={`variants.${index}.sku`}
            control={control}
            render={({ field: controlField }) => (
              <div className="space-y-2">
                <Label htmlFor={`variant-${index}-sku`}>{t('sku')}</Label>
                <Input id={`variant-${index}-sku`} {...controlField} value={controlField.value || ''} />
                {variantErrors?.sku && (
                  <p className="mt-1 text-xs leading-tight text-red-600">
                    {variantErrors.sku.message as string}
                  </p>
                )}
              </div>
            )}
          />
        </div>

        <Controller
          name={`variants.${index}.description`}
          control={control}
          render={({ field: controlField }) => (
            <div className="space-y-2">
              <Label htmlFor={`variant-${index}-description`}>
                {t('description')}
              </Label>
              <Textarea
                id={`variant-${index}-description`}
                {...controlField}
                value={controlField.value || ''}
                rows={2}
              />
              {variantErrors?.description && (
                <p className="mt-1 text-xs leading-tight text-red-600">
                  {variantErrors.description.message as string}
                </p>
              )}
            </div>
          )}
        />

        <div className="space-y-4">
          <Controller
            name={`variants.${index}.imageUrl`}
            control={control}
            render={({ field: controlField }) => (
              <div className="space-y-2">
                <Label htmlFor={`variant-${index}-imageUrl`}>{t('image')}</Label>
                <FormImageUpload
                  id={`variant-${index}-imageUrl`}
                  name={`variant-${index}-image`}
                  url={controlField.value || ''}
                  onImageChange={(url) => controlField.onChange(url || '')}
                />
              </div>
            )}
          />

          <Controller
            name={`variants.${index}.grams`}
            control={control}
            render={({ field: controlField }) => (
              <div className="space-y-2">
                <Label htmlFor={`variant-${index}-grams`}>
                  {t('weight')}
                </Label>
                <Input
                  id={`variant-${index}-grams`}
                  type="number"
                  value={controlField.value || ''}
                  onChange={(e) => controlField.onChange(parseInt(e.target.value) || null)}
                />
              </div>
            )}
          />
        </div>

        <div className="flex items-center gap-4">
          <Controller
            name={`variants.${index}.default`}
            control={control}
            render={({ field: controlField }) => (
              <div className="flex items-center space-x-2">
                <Switch
                  id={`variant-${index}-default`}
                  checked={controlField.value}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      onSetDefault();
                    }
                  }}
                  disabled={controlField.value}
                />
                <Label htmlFor={`variant-${index}-default`}>
                  {t('defaultVariant')}
                </Label>
              </div>
            )}
          />

          <Controller
            name={`variants.${index}.active`}
            control={control}
            render={({ field: controlField }) => (
              <div className="flex items-center space-x-2">
                <Switch
                  id={`variant-${index}-active`}
                  checked={controlField.value}
                  onCheckedChange={controlField.onChange}
                />
                <Label htmlFor={`variant-${index}-active`}>{t('enabled')}</Label>
              </div>
            )}
          />
        </div>

        <div className="mt-4 border-t pt-4">
          <Controller
            name={`variants.${index}.prices`}
            control={control}
            render={({ field: controlField }) => (
              <ProductPricesForm
                prices={controlField.value || []}
                onUpdatePrices={controlField.onChange}
                variantIndex={index}
                storeId={storeId}
                hideAddPrice={hideAddPrice}
                hideRemovePrice={hideRemovePrice}
              />
            )}
          />
        </div>
      </CardContent>
    </Card>
  );
};
